/**
 * Room codes.
 *
 * A room code is read aloud over the phone, typed on a laptop, or squinted at
 * across a room. Every design choice here follows from that.
 *
 *   - The alphabet excludes anything that looks like something else. No O or 0,
 *     no I, L or 1, no S or 5, no B or 8. What remains cannot be misread.
 *   - Codes carry a check character, so a mistyped code is rejected instantly
 *     and locally, with a clear message, rather than producing a confusing
 *     "room not found" after a network round trip.
 *   - Input is normalised aggressively. Lower case, stray spaces, dashes in the
 *     wrong place and a pasted URL all resolve to the same code.
 *
 * Five random characters over a 24 letter alphabet is about 7.9 million
 * combinations. For a service where two people share a code that lives for the
 * length of a film, that is ample, and the check character means a random
 * guess is rejected 23 times out of 24 before it ever reaches the relay.
 */

/**
 * Characters that cannot be confused with one another when read or typed.
 *
 * The length being **prime** is load bearing, not incidental. The check
 * character is a weighted sum taken modulo the alphabet size, and it can only
 * catch a mistyped character if the position weight shares no factor with that
 * modulus. A 25 character alphabet would silently accept a whole family of
 * typos in the fourth position, because weight 5 and modulus 25 share a factor.
 * At 23, every weight is coprime with the modulus and every single character
 * error is caught. Z and 2 were the two dropped to get here, which is no loss:
 * they are easily confused with each other in many typefaces.
 */
export const ROOM_CODE_ALPHABET = 'ACDEFGHJKMNPQRTUVWXY479';

const ALPHABET = ROOM_CODE_ALPHABET;

/** Number of random characters, before the check character is appended. */
const BODY_LENGTH = 5;

/** Total length of a complete code including its check character. */
export const ROOM_CODE_LENGTH = BODY_LENGTH + 1;

/**
 * Characters that people commonly substitute for members of the alphabet.
 * Applied during normalisation so an honest mistake is silently forgiven.
 */
const CONFUSABLES: Readonly<Record<string, string>> = {
  '0': 'Q',
  O: 'Q',
  '1': '7',
  I: 'J',
  L: 'J',
  '5': 'X',
  S: 'X',
  '8': 'W',
  B: 'W',
  '6': 'G',
  '3': 'E',
};

/**
 * Z and 2 are deliberately absent from both the alphabet and the substitution
 * table. They are never generated, so seeing one means the user made a mistake
 * that the length and checksum rules should surface rather than paper over.
 */

/**
 * Source of randomness, injectable so tests are deterministic.
 * Defaults to the platform CSPRNG.
 */
export type RandomBytes = (length: number) => Uint8Array;

const defaultRandomBytes: RandomBytes = (length) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
};

/**
 * Generate a fresh room code, already formatted for display.
 *
 * Uses rejection sampling rather than a plain modulo. Modulo over 256 would
 * make the first six letters of the alphabet slightly more likely than the
 * rest, and biased codes are a bad habit to build into anything that also
 * gates access.
 */
export function generateRoomCode(randomBytes: RandomBytes = defaultRandomBytes): string {
  const limit = Math.floor(256 / ALPHABET.length) * ALPHABET.length;
  let body = '';

  while (body.length < BODY_LENGTH) {
    const batch = randomBytes(BODY_LENGTH * 2);
    for (const byte of batch) {
      if (body.length >= BODY_LENGTH) break;
      if (byte >= limit) continue; // Biased tail of the byte range, discard.
      body += ALPHABET[byte % ALPHABET.length];
    }
  }

  return formatRoomCode(body + checkCharacter(body));
}

/**
 * Reduce anything the user might have supplied to a bare canonical code.
 *
 * Accepts a raw code, a formatted one, lower case, extra whitespace, and a
 * full invite URL, because all four are things people actually paste.
 */
export function normaliseRoomCode(input: string): string {
  // If it looks like a URL, keep only the last path or query segment.
  let text = input.trim();
  const urlMatch = /(?:[?&]room=|\/)([^/?&#\s]+)\s*$/.exec(text);
  if (text.includes('/') || text.includes('room=')) {
    if (urlMatch?.[1] !== undefined) text = urlMatch[1];
  }

  let out = '';
  for (const rawChar of text.toUpperCase()) {
    if (!/[A-Z0-9]/.test(rawChar)) continue; // Drop dashes, spaces, punctuation.
    const mapped = CONFUSABLES[rawChar] ?? rawChar;
    if (ALPHABET.includes(mapped)) out += mapped;
  }
  return out;
}

/** Insert the display separator: three characters, a dash, three characters. */
export function formatRoomCode(code: string): string {
  const bare = normaliseRoomCode(code);
  if (bare.length !== ROOM_CODE_LENGTH) return bare;
  return `${bare.slice(0, 3)}-${bare.slice(3)}`;
}

/** Why a code was rejected, so the interface can say something specific. */
export type RoomCodeProblem = 'empty' | 'too-short' | 'too-long' | 'checksum';

export type RoomCodeValidation =
  | { readonly ok: true; readonly code: string; readonly formatted: string }
  | { readonly ok: false; readonly problem: RoomCodeProblem; readonly message: string };

/**
 * Validate a code locally, before any network call.
 *
 * Catching a typo here rather than at the relay is the difference between
 * "that code has a typo in it" and a puzzling failure thirty seconds later.
 */
export function validateRoomCode(input: string): RoomCodeValidation {
  const code = normaliseRoomCode(input);

  if (code.length === 0) {
    return { ok: false, problem: 'empty', message: 'Enter a room code.' };
  }
  if (code.length < ROOM_CODE_LENGTH) {
    return { ok: false, problem: 'too-short', message: 'That code is too short.' };
  }
  if (code.length > ROOM_CODE_LENGTH) {
    return { ok: false, problem: 'too-long', message: 'That code is too long.' };
  }

  const body = code.slice(0, BODY_LENGTH);
  if (code[BODY_LENGTH] !== checkCharacter(body)) {
    return {
      ok: false,
      problem: 'checksum',
      message: 'That code has a typo in it. Check it and try again.',
    };
  }

  return { ok: true, code, formatted: formatRoomCode(code) };
}

/** Convenience predicate for places that do not need the reason. */
export function isValidRoomCode(input: string): boolean {
  return validateRoomCode(input).ok;
}

/**
 * Weighted modular checksum over the alphabet.
 *
 * The positional weights are what catch transpositions. An unweighted sum
 * would happily accept two adjacent characters swapped, which is one of the
 * most common typing mistakes there is.
 */
function checkCharacter(body: string): string {
  let sum = 0;
  for (let i = 0; i < body.length; i += 1) {
    const value = ALPHABET.indexOf(body[i]!);
    sum += value * (i + 2);
  }
  return ALPHABET[sum % ALPHABET.length]!;
}
